/**
 * WHAT IS INSIDE A BUILDING — the plan every level of the interior reads, and the only place it is
 * decided. What CityLayout is to the block, this is to the building: pure, seed-derived, no state.
 *
 * THE LADDER IS ARITHMETIC, NOT TASTE. Each cutoff below is the level at which the thing it names
 * stops being representable at all:
 *
 *  - A 1.5 m window cannot exist in a 2 m voxel, so openings stop at lod 2.
 *  - A 3.5 m storey is 7 samples at lod 1 (slab + six clear) and THREE AND A HALF at lod 2 — so every
 *    slab would land on a different sample and the floors would visibly wander up and down the tower.
 *    The same failure as an unfloored canopy dot or region wall, and the reason floors stop at lod 1.
 *  - A partition is one fine voxel. There is no coarser form, so rooms and stairs are lod 0.
 *
 * HOLLOWNESS IS TIED TO WINDOWS, NOT TO FLOORS. At lod 2 a solid interior makes a window a one-voxel
 * dimple instead of a dark opening, so the shell has to be hollow wherever openings are drawn. Above
 * that it stays solid: hollowing at lod 3 doubles the mesh for an interior nobody can see into.
 *
 * AND NOTHING HERE CARVES. "Hollow" means the shell DECLINES TO WRITE those voxels, and an opening
 * means the same — floors, partitions and stairs then write into air that was never filled. That
 * keeps the no-carve rule that protects the terrain and the region wall exactly as it was, keeps AABB
 * containment trivially true, and means emit order carries no new obligation.
 *
 * ---------------------------------------------------------------------------------------------
 * THE PLAN ITSELF, and the four rules that shape it. Each one exists because the obvious layout
 * produces a building you can see is wrong from inside it.
 *
 * 1. THE SPINE IS CENTRED AND THE SHAFTS SIT AT ITS ENDS. One stair in a random corner means every
 *    trip across the tower is a trip to that corner; two, one at each end of the corridor, means you
 *    are never more than half a plate from a flight. It also makes the plan symmetric, which is what
 *    lets the corridor be centred on the building's own axis instead of chasing the shaft.
 *
 * 2. NOTHING TOUCHES A SHAFT — and the CORRIDOR is what guarantees it. The band is the shaft's own
 *    width plus STAIR_CLEARANCE on each side, and the rooms are the strips OUTSIDE that band, so a
 *    partition is two metres clear of a flight wherever it lands. That is a property of the layout,
 *    not something the drawing code has to check, and it is why the rooms can run the whole length
 *    of the plate instead of being banished to the middle of it.
 *
 * 3. EVERY INTERIOR WALL THAT MEETS THE SHELL LANDS BETWEEN TWO WINDOWS. Not by correcting a wall
 *    that landed badly, but by BUILDING THE ROOMS ON THE WINDOW LATTICE: `roomCuts` returns the gaps
 *    themselves, so a partition has nowhere else to be. It is not cosmetic — a partition arriving
 *    mid-window leaves a slot of daylight down the join, because the shell subtracts its opening from
 *    the shell alone. Only partitions reach the shell: the corridor walls stop inside the zone.
 *
 * 4. THE CORES ARE PLACED AGAINST THE NARROWEST TIER. A shaft is vertical, so it must fit the
 *    smallest footprint the tower ever has, or it exits through the wall at a setback. Same rule as
 *    the old single core, applied to both.
 *
 * 5. THE GROUND FLOOR HAS NO WALL. It is a colonnade: `postDistAlong` keeps a post on the window
 *    lattice and opens everything between, full height, so the lobby reads as a tower standing on
 *    piers rather than a box with big windows.
 *
 * AND ONE RULE ABOUT THICKNESS, because getting it wrong is invisible until it is on screen.
 * `minThicknessFine` is a FLOOR UNDER AN AUTHORED SIZE, never the size itself. It guarantees the
 * lattice can hold a surface at all — measured, zero holes at every level — and nothing more: a slab
 * sitting exactly ON the floor is sqrt(2) samples thick, reconstructs at 84% of that, and has barely
 * a sample between its two crossings for the mesher to interpolate. It looks like what it is, the
 * thinnest thing this system permits. So every wall here is authored ABOVE the floor and the floor
 * only bites at coarse levels, which is what it is for. The parapet was authored at 0.25 m — BELOW
 * it — and rendered at 0.297 m with visible faceting until it was given a real dimension.
 */
import { hashInt2 } from '../../util/rng.js';
import { VOXEL_SCALE } from '../../voxel/constants.js';
/** Coarsest LOD at which the shell is hollow and carries its windows and door. */
export const MAX_SHELL_LOD = 2;
/** Coarsest LOD at which floor slabs are drawn. */
export const MAX_FLOOR_LOD = 1;
/** Coarsest LOD at which the corridor, the partitions and the stairs are drawn. */
export const MAX_ROOM_LOD = 0;
// ---- Dimensions, in metres. Fine voxels are 0.25 m, so each is a whole number of them. ----
/** 3.5 m: 14 fine voxels, 7 samples at lod 1. See the ladder note above. */
export const STOREY_HEIGHT = 3.5;
export const SLAB_THICKNESS = 0.5;
/**
 * THREE FINE VOXELS, AND THE THIRD ONE IS LOAD-BEARING. A shell has to show brick to the street and
 * the interior material to the room, and SurfaceNets takes ONE material per cell from the corner of
 * its 2x2x2 block with the highest weight. At two voxels the samples across a wall read [0, 0.5, 0]
 * and the CORE wins every vote, so both faces were coloured by a sample belonging to neither and the
 * material dithered along the wall with the sampling phase. At three the core is a voxel further from
 * each face, so a face cell holds that face's own boundary sample and air, and nothing else.
 */
export const WALL_THICKNESS = 0.75;
export const PARTITION_THICKNESS = 0.25;
export const CORRIDOR_WIDTH = 12;
/**
 * A straight flight: 7 risers of 0.5 m, and a landing slot at the foot of each, so the run is eight
 * tread-slots long.
 *
 * THE SHAFT IS TWO FLIGHTS WIDE, and that is not a style choice. A switchback over a single-width
 * core puts flight N+1 directly over flight N, and the arithmetic is fatal: the clearance above
 * flight N's tread at slot k is `28 - 4k` fine voxels, so it is 1.0 m two steps from the top and
 * ZERO at the top — the next flight's first step lands on the previous flight's last tread and seals
 * the stairwell. The reachability test caught exactly that. Side by side, the space above a flight is
 * open until the storey TWO above reuses the half, which is 28 voxels = 7 m of headroom.
 */
export const STAIR_RISER = 0.25;
/**
 * Going per step. TWICE THE RISER, so the flight sits at about 27 degrees instead of 45 — the
 * difference between a stair and a ladder. Two fine voxels, so a tread lands on the lattice exactly.
 *
 * A RISER OF ONE VOXEL IS AT THE LATTICE'S OWN FLOOR, so the tread's SLAB is floored separately where
 * it is drawn: a step 1 voxel thick reconstructs at 84% of that and the flight comes out lacy. The
 * pitch is the pair's ratio and is unchanged; only the step count doubled.
 */
export const STAIR_TREAD = 0.5;
/**
 * ...AND THERE IS A LANDING AT BOTH ENDS OF THE RUN, three slots deep.
 *
 * One slot is 0.5 m: a single tread with a hole beside it, not a platform, and it read as a missing
 * landing because that is what it was. Three is 1.5 m — enough to stand on and turn.
 *
 * BOTH ENDS, and that is the part that is not obvious. A flight climbs to the far end of the run, so
 * a landing THERE is standing over the last treads of the flight below it: with only a bottom landing
 * the platform at the turn sealed the headroom three treads down and the stairwell stopped being
 * climbable. The turn is a place in the shaft, not a part of a flight, so the run carries one at each
 * end and consecutive flights share them.
 *
 * A LANDING IS AS DEEP AS A FLIGHT IS WIDE, which is what makes the shaft's plan read as two
 * squares: `STAIR_LANDING_SLOTS * STAIR_TREAD == CORE_WIDTH / 2`. The run follows from that rather
 * than the other way round — two landings plus fourteen risers is twenty-six slots, and
 * `CORE_RUN / 26 == STAIR_TREAD`, so every tread lands on the lattice exactly.
 *
 * THE COST IS THE SECOND SHAFT, and it is worth being blunt about it. A 27-degree flight needs 7 m of
 * run where a 45-degree one needed 3.5, and with its two landings the shaft is 13 m long against a
 * narrowest plate of about 20 m. Two of those do not fit on any tower this generator makes, so a
 * building now has ONE stair. Shortening the tread is the lever that buys the pair back.
 */
export const STAIR_LANDING_SLOTS = 6;
export const CORE_RUN = 13;
/** Two 3 m flights side by side. */
export const CORE_WIDTH = 6;
/** The shaft does not narrow: the landing's depth is derived from this, so a narrowed one would stop
 *  being a landing. A plate that cannot hold it has no interior, as before. */
export const CORE_WIDTH_MIN = 6;
/** Open floor kept clear all round a shaft, so no wall ever lands on the edge of a flight. */
export const STAIR_CLEARANCE = 2;
/** Plate a second shaft must leave between the two halls, or the tower gets only one. */
export const MIN_ROOM_RUN = 2;
export const ROOM_WIDTH = 8;
export const WINDOW_SIZE = 1.5;
export const WINDOW_SILL = 1;
export const WINDOW_PITCH = 4;
export const DOOR_WIDTH = 1;
export const DOOR_HEIGHT = 2;
/**
 * Storeys the ground floor swallows: it is one double-height open volume with no slab between, no
 * partitions and no rooms — a lobby held up by columns, with the flights as its only furniture.
 */
export const LOBBY_STOREYS = 2;
export const COLUMN_SIZE = 0.75;
/** Width of a pier in the lobby's arcade, along the face it stands in. */
export const LOBBY_POST_WIDTH = 0.75;
/** Where the arch between two piers springs, in metres above the lobby floor. Its crown is that plus
 *  half the gap, so a wider bay gives a taller arch and the spandrel above it stays solid. */
export const ARCH_SPRING = 3;
/** The parapet round every cap — the terraces AND the roof, which is what makes the hatch safe.
 *  As thick as the shell wall, deliberately: see the thickness rule in the header. */
export const PARAPET_HEIGHT = 1;
export const PARAPET_THICKNESS = 0.5;
const ROOM_SALT = 0x0011a5;
const unit = (h) => (h >>> 0) / 0x100000000;
/**
 * Storeys in a height.
 *
 * THE EPSILON IS LOAD-BEARING. `blockProfileHeight` now returns a whole number of storeys, and the
 * roof access depends on `storeys * storeyH` landing exactly on the last tier's top. A float that
 * comes back 3.4999999 floors to one storey short and the top flight climbs into a solid roof.
 */
export function storeyCount(heightFine, storeyH) {
    return Math.max(1, Math.floor((heightFine * VOXEL_SCALE) / storeyH + 1e-6));
}
/** The buildable plate of a tier: its footprint inset by the shell. Null where nothing fits. */
export function tierPlate(halfU, halfV) {
    const u = halfU - WALL_THICKNESS, v = halfV - WALL_THICKNESS;
    return u > 0 && v > 0 ? { u0: -u, u1: u, v0: -v, v1: v } : null;
}
/**
 * Which axis the corridor runs along — the plate's longer one.
 *
 * DECIDED ONCE, AGAINST THE NARROWEST TIER, and stored. Tiers shrink by a uniform factor so the
 * aspect ratio is preserved, but the plate is the tier inset by a CONSTANT, which is not uniform: a
 * nearly square tower can flip its long axis between tiers, and the shaft would then be crosswise to
 * the corridor on half its storeys.
 */
export function spineAlongU(p) {
    return (p.u1 - p.u0) >= (p.v1 - p.v0);
}
function toRect(alongU, a0, a1, b0, b1) {
    return alongU ? { u0: a0, u1: a1, v0: b0, v1: b1 } : { u0: b0, u1: b1, v0: a0, v1: a1 };
}
/** The plate's extent along the spine. */
export function alongSpan(p, alongU) {
    return alongU ? { lo: p.u0, hi: p.u1 } : { lo: p.v0, hi: p.v1 };
}
/** ...and across it. */
export function acrossSpan(p, alongU) {
    return alongU ? { lo: p.v0, hi: p.v1 } : { lo: p.u0, hi: p.u1 };
}
/**
 * THE STAIR SHAFTS — one at each end of the spine, centred across it.
 *
 * Two where the plate is long enough to hold both halls and still leave a room between them, one
 * where it is not, none where even one hall will not fit (that tower has no interior, exactly as a
 * tower too slim for the old single core had none).
 *
 * The shaft NARROWS before it disappears: a plate that cannot hold the full 8 m width gets whatever
 * it can, down to CORE_WIDTH_MIN. Two 2 m flights is what the tower had before, so the floor of this
 * ramp is the old behaviour rather than a new failure mode.
 */
export function coreRects(p, alongU) {
    const len = alongSpan(p, alongU), acr = acrossSpan(p, alongU);
    const across = Math.min(CORE_WIDTH, (acr.hi - acr.lo) - 2 * STAIR_CLEARANCE);
    if (across < CORE_WIDTH_MIN)
        return [];
    const ac = (acr.lo + acr.hi) * 0.5, ah = across * 0.5;
    const hall = CORE_RUN + 2 * STAIR_CLEARANCE;
    if (len.hi - len.lo < hall)
        return [];
    const out = [toRect(alongU, len.lo + STAIR_CLEARANCE, len.lo + STAIR_CLEARANCE + CORE_RUN, ac - ah, ac + ah)];
    // TWO SHAFTS NEED ROOM FOR TWO SHAFTS, not for two halls. The clearance is what keeps WALLS off a
    // flight, and there are none between the two — it is all corridor — so the halls may abut. Asking
    // for two disjoint halls cost every tiered tower its second stair for the sake of nothing.
    if (len.hi - len.lo >= 2 * (CORE_RUN + STAIR_CLEARANCE) + MIN_ROOM_RUN) {
        out.push(toRect(alongU, len.hi - STAIR_CLEARANCE - CORE_RUN, len.hi - STAIR_CLEARANCE, ac - ah, ac + ah));
    }
    return out;
}
/**
 * HOW WIDE THE CORRIDOR HAS TO BE, whatever the config asked for.
 *
 * The shaft sits IN the corridor, and the clearance either side of it is the whole reason no wall can
 * ever land on a flight. So this is a floor the config may widen and may not undercut — the invariant
 * lives in the code, not in a number a user can drag.
 */
export function corridorWidthFor(configured) {
    return Math.max(configured, CORE_WIDTH + 2 * STAIR_CLEARANCE);
}
/**
 * THE CORRIDOR SPINE — a band along the plate's longer axis, centred on the building's own axis and
 * running its whole length, with a shaft standing in it at each end.
 *
 * This is the piece that makes the plan connected without a search: every room opens onto the spine
 * and the spine contains both shafts, so `room -> corridor -> stairs` holds for every seed. It is
 * centred rather than aimed at a shaft because the shafts are symmetric about the same axis, which is
 * the whole payoff of putting them at the ends.
 */
export function corridorBand(plate, alongU, width) {
    const h = width * 0.5;
    return alongU
        ? { u0: plate.u0, u1: plate.u1, v0: -h, v1: h }
        : { u0: -h, u1: h, v0: plate.v0, v1: plate.v1 };
}
// ============== Windows, and the walls that meet them ==============
/**
 * Signed distance along a face to the nearest window's edge — negative inside one, +Infinity where
 * the face is too short to carry any.
 *
 * A DISTANCE RATHER THAN A PREDICATE, because a boolean is what makes an opening's edge jagged: the
 * reveal needs its own band or the hole is a staircase punched through a smooth wall
 * (voxel/surfaceBand.ts). `windowAt` is its sign, so the two cannot drift.
 *
 * ANALYTIC RATHER THAN A LIST OF POSITIONS, because this is asked per voxel of every wall in the
 * city: building the offsets would allocate an array per tier per column. A full window's worth of
 * wall is kept at each corner, and the row is symmetric about the building's own axis, so faces
 * agree where they meet.
 */
/**
 * The nearest window's CENTRE along a face, or +Infinity where the face carries none — the place a
 * lamp goes if it is to be seen from outside. Same lattice `windowDistAlong` measures against.
 */
export function windowCentreAlong(coord, half, pitch, size) {
    const usable = half * 2 - size * 2;
    if (usable <= 0)
        return Infinity;
    const n = Math.max(1, Math.floor(usable / Math.max(0.5, pitch)));
    const step = usable / n;
    const t = (coord + usable * 0.5) / step;
    const c = Math.round(t) * step - usable * 0.5;
    return Math.abs(c) > usable * 0.5 + 1e-9 ? Infinity : c;
}
export function windowDistAlong(coord, half, pitch, size) {
    const usable = half * 2 - size * 2;
    if (usable <= 0)
        return Infinity;
    const n = Math.max(1, Math.floor(usable / Math.max(0.5, pitch)));
    const step = usable / n;
    const t = (coord + usable * 0.5) / step;
    const r = (t - Math.round(t)) * step;
    const along = Math.abs(r) - size * 0.5;
    const within = Math.abs(coord) - usable * 0.5;
    return along > within ? along : within;
}
/** Is this offset along a face inside a window? The sign of `windowDistAlong`, and nothing else. */
export function windowAt(coord, half, pitch, size) {
    return windowDistAlong(coord, half, pitch, size) < 0;
}
/**
 * The window lattice of a face, as the gaps BETWEEN its windows — which is the only place an
 * interior wall may meet it.
 *
 * `windowDistAlong` puts window centres at `-usable/2 + m*step` for m = 0..n, so the gaps are the
 * midpoints, `m + 0.5`. Returns null where the face has no windows, or where the gap is too narrow
 * to swallow the wall's thickness with a clear margin either side — in both cases the caller leaves
 * its wall where it was, because nudging it would be a lie about a constraint that cannot be met.
 */
function windowGaps(half, pitch, size, thickness) {
    const usable = half * 2 - size * 2;
    if (usable <= 0)
        return null;
    const n = Math.max(1, Math.floor(usable / Math.max(0.5, pitch)));
    const step = usable / n;
    if (step - size < thickness)
        return null;
    return { first: -usable * 0.5 + step * 0.5, step, count: n };
}
/**
 * WHERE THE ROOMS ARE CUT — every cut on a window gap, by construction rather than by correction.
 *
 * The slices come out at about `roomWidth` because the gaps are one window pitch apart and the two
 * are the same number at the defaults; where they differ the stride rounds and the rooms land on the
 * nearest whole number of windows, which is what a real plan does too.
 *
 * The per-storey offset is measured in WHOLE STEPS, so two floors of a tower are not the same plan
 * and every cut still lands in a gap. At the default pitch the stride is 1 and the offset is
 * therefore always 0 — the jitter only appears once rooms are wider than windows.
 */
export function roomCuts(lo, hi, half, pitch, size, thickness, roomWidth, seed) {
    const g = windowGaps(half, pitch, size, thickness);
    const out = [];
    if (!g)
        return out;
    const stride = Math.max(1, Math.round(Math.max(1, roomWidth) / g.step));
    const offset = stride > 1 ? (hashInt2(seed, ROOM_SALT) >>> 0) % stride : 0;
    for (let m = offset; m < g.count; m += stride) {
        const c = g.first + m * g.step;
        if (c >= lo && c <= hi)
            out.push(c);
    }
    return out;
}
/**
 * Where the piers of the lobby's arcade stand along a face, in metres from its centre. The same
 * lattice `archOpeningDist` cuts its bays from, so anything hung on a pier lands on one.
 */
export function postPositions(half, pitch, width) {
    const g = windowGaps(half, pitch, WINDOW_SIZE, width);
    const out = [];
    if (!g)
        return out;
    for (let m = 0; m < g.count; m++)
        out.push(g.first + m * g.step);
    return out;
}
/**
 * THE LOBBY'S ARCADE — the signed distance to the OPENING between two piers, negative inside it.
 * The ground floor has no wall at all: this is what stands in its place.
 *
 * AN ARCH RATHER THAN A SLOT, which is the whole difference between a building on piers and a
 * building with holes in it. Below the springing the opening is a plain rectangle; above it, a
 * half-disc of the bay's own half-width, so the crown rises with the bay and the spandrel over it
 * stays solid. Both are exact distances, so the reveal carries a band like every other surface here
 * and the arch is smooth rather than a staircase (voxel/surfaceBand.ts).
 *
 * ON THE WINDOW LATTICE, so a pier lands under the line the partitions above land on and the tower
 * reads as one thing from the street. The CORNERS stay solid, by the same keep-out `windowDistAlong`
 * uses — a building open at its corners has nothing holding the two faces together, and looks it.
 */
export function archOpeningDist(coord, half, pitch, width, heightAboveFloor) {
    const g = windowGaps(half, pitch, WINDOW_SIZE, width);
    // Too short a face to carry a lattice: leave it solid rather than guess.
    if (!g)
        return width;
    // A BAY is the space BETWEEN two piers, so its centre is a half-step off the pier lattice and there
    // is one fewer of them than there are piers. Past the outermost pier there is no bay at all, which
    // is what makes the ends of each face read as a wider pier rather than as a ragged opening.
    if (g.count < 2)
        return width;
    const m = Math.max(0, Math.min(g.count - 2, Math.floor((coord - g.first) / g.step)));
    const a = Math.abs(coord - (g.first + (m + 0.5) * g.step));
    const halfGap = (g.step - width) * 0.5;
    if (halfGap <= 0)
        return width;
    const dy = heightAboveFloor - ARCH_SPRING;
    const shaft = Math.max(a - halfGap, dy); // the square-headed part, below the spring
    const head = Math.hypot(a, dy > 0 ? dy : 0) - halfGap; // ...and the half-disc above it
    const open = shaft < head ? shaft : head;
    // The corner keep-out wins over the opening wherever it applies.
    const corner = Math.abs(coord) - (half - WINDOW_SIZE);
    return open > corner ? open : corner;
}
/**
 * Which room slices a window along the spine touches.
 *
 * INDEX RANGE, NOT A LIST — the same rule as the storey range one level up. A LOD-0 chunk crossing a
 * 30 m plate expands two or three rooms rather than building all of them for `aabbOverlaps` to drop
 * the rest, and that is the whole reason the hierarchy is worth having.
 */
export function roomRange(cuts, lo, hi) {
    const n = cuts.length - 1; // rooms lie BETWEEN consecutive cuts
    if (n <= 0)
        return { kLo: 0, kHi: -1 };
    let kLo = 0;
    while (kLo < n - 1 && cuts[kLo + 1] <= lo)
        kLo++;
    let kHi = n - 1;
    while (kHi > kLo && cuts[kHi] >= hi)
        kHi--;
    return { kLo, kHi };
}
/** Slice `k` of the strip on `side` (-1 = the low side of the spine, +1 = the high side). */
export function roomRectAt(plate, band, alongU, cuts, k, side) {
    const acr = acrossSpan(plate, alongU), bnd = acrossSpan(band, alongU);
    return side < 0
        ? toRect(alongU, cuts[k], cuts[k + 1], acr.lo, bnd.lo)
        : toRect(alongU, cuts[k], cuts[k + 1], bnd.hi, acr.hi);
}
/** Is this point inside the rectangle? */
export function inRect(r, u, v) {
    return u >= r.u0 && u < r.u1 && v >= r.v0 && v < r.v1;
}
/** Do these rectangles overlap? */
export function overlapsRect(a, b) {
    return a.u0 < b.u1 && a.u1 > b.u0 && a.v0 < b.v1 && a.v1 > b.v0;
}
/** The tier a height above the seat falls in. Tiers are bottom-up and each is inside the one below. */
export function tierAt(tiers, above) {
    for (const t of tiers)
        if (above <= t.top)
            return t;
    return null;
}
/**
 * Which face gets the front door, as 0..3 = +u, -u, +v, -v.
 *
 * Any of them is a street frontage — a block in this grid is bounded by roads on all four sides — so
 * the roll is free rather than being aimed at a particular road.
 */
export function doorSide(coreSeed) {
    return Math.floor(unit(hashInt2(coreSeed, 0xd002)) * 4) % 4;
}
//# sourceMappingURL=BuildingPlan.js.map